import React, { useEffect } from "react";
import router, { useRouter } from "next/router";
import cx from "classnames";
import { useTranslation } from "next-i18next";

import { Label } from "common/ui/Label";
import { Stack } from "common/ui/Stack";
import { Icon } from "common/ui/Icon";
import { DropdownMenu } from "common/ui/DropdownMenu";

import styles from "./Pagination.module.scss";

interface Props {
  totalNumOfItems: number,
}

export function Pagination({ totalNumOfItems }: Props) {
  const { t } = useTranslation("common");
  const { query, isReady } = useRouter();

  const currentPage = Number(query.page);
  const pageSize = Number(query.pageSize);

  const startCount = totalNumOfItems > 0
    ? currentPage * pageSize + 1
    : 0;

  const endCount = totalNumOfItems < (currentPage + 1) * pageSize
    ? totalNumOfItems
    : (currentPage + 1) * pageSize;

  const nextPage = () => {
    router.push({ query: { ...query, page: currentPage + 1 }});
  };

  const previousPage = () => {
    router.push({ query: { ...query, page: currentPage - 1 }});
  };

  const changePageSize = (size: number) => {
    router.push({ query: { ...query, pageSize: size, page: 0 }});
  };

  useEffect(() => {
    if (
      !isReady
      || (query.page && query.pageSize)
    ) {
      return;
    }

    router.replace({ query: { ...query, page: 0, pageSize: 10 } });
  }, [query, isReady]);

  return (
    totalNumOfItems > 10
    ? <div className={styles.Pagination}>
        <Stack justifyContent="space-between">
          <Stack gap={24} alignItems="center">
            <Stack>
              <button
                className={cx(styles.Btn, styles.LeftBtn)}
                onClick={previousPage}
                disabled={currentPage === 0}
              >
                <Icon icon="chevronLeftMinor" />
              </button>
              <button
                className={cx(styles.Btn, styles.RightBtn)}
                onClick={nextPage}
                disabled={endCount === totalNumOfItems}
              >
                <Icon icon="chevronRightMinor" />
              </button>
            </Stack>
            <Label variant="l4">
              {t("pagination.counter", {
                startCount: startCount,
                endCount: endCount,
                totalCount: totalNumOfItems
              })}
            </Label>
          </Stack>
          <Stack gap={8} alignItems="center">
            <Label variant="l4">{t("pagination.show")}</Label>
            <DropdownMenu
              selected={{ id: pageSize, item: pageSize }}
              items={[
                { id: "10", name: 10, onClick: () => changePageSize(10) },
                { id: "25", name: 25, onClick: () => changePageSize(25) },
                { id: "50", name: 50, onClick: () => changePageSize(50) },
              ]}
              alignRight
              top
            />
            <Label variant="l4">{t("pagination.perPage")}</Label>
          </Stack>
        </Stack>
      </div>
  : null
  )
}

